import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/auth-context';

interface Capabilities {
  canManageUsers: boolean;
  canManageRoles: boolean;
  canManageSettings: boolean;
}

const DEFAULT_CAPABILITIES: Capabilities = {
  canManageUsers: false,
  canManageRoles: false,
  canManageSettings: false,
};

/**
 * Returns the effective RBAC capabilities for the current user.
 * Native admins and SuperAdmins are granted management capabilities, matching backend RBAC.
 * For regular users, capabilities are derived from their assigned RBAC roles.
 */
export function useCapabilities(): { capabilities: Capabilities; loading: boolean } {
  const { admin, tenant, isSuperAdmin, isAuthenticated, isTenantUser } = useAuth();
  const key = JSON.stringify([tenant?.id, admin?.id, isSuperAdmin, isAuthenticated, isTenantUser]);
  const [state, setState] = useState<{ key: string; capabilities: Capabilities; loading: boolean }>({
    key: '', capabilities: DEFAULT_CAPABILITIES, loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated || !admin?.id) {
      setState({ key, capabilities: DEFAULT_CAPABILITIES, loading: false });
      return;
    }

    if (isSuperAdmin || isTenantUser === false) {
      setState({ key, capabilities: { canManageUsers: true, canManageRoles: true, canManageSettings: true }, loading: false });
      return;
    }

    setState({ key, capabilities: DEFAULT_CAPABILITIES, loading: true });
    api.roles
      .getUserRoles(admin.id)
      .then((userRoles) => {
        const merged: Capabilities = { ...DEFAULT_CAPABILITIES };
        for (const ur of userRoles) {
          const perms = ur.role?.permissions ?? ur.permissions ?? {};
          if (perms.canManageUsers) merged.canManageUsers = true;
          if (perms.canManageRoles) merged.canManageRoles = true;
          if (perms.canManageSettings) merged.canManageSettings = true;
        }
        if (!cancelled) setState({ key, capabilities: merged, loading: false });
      })
      .catch(() => {
        if (!cancelled) setState({ key, capabilities: DEFAULT_CAPABILITIES, loading: false });
      });
    return () => { cancelled = true; };
  }, [key, admin?.id, isSuperAdmin, isAuthenticated, isTenantUser]);

  // Do not render the previous tenant's capabilities before the effect runs.
  return state.key === key
    ? { capabilities: state.capabilities, loading: state.loading }
    : { capabilities: DEFAULT_CAPABILITIES, loading: true };
}
